import {
  DataTag,
  QueryClient,
  QueryFunction,
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'

import { customInstance, ErrorType } from '@/features/auth/axios-config'
import { ErrorResponse } from '@/schema'

import { getFileContentByBlobURL, SecondParameter } from '../services'
import { GetDocumentContentParams } from '../types'

const getGetFileContentByBlobURLQueryKey = (
  params?: GetDocumentContentParams
) => {
  return [
    'tfl',
    'html',
    ...(params ? [params] : []),
    'version-comparison',
  ] as const
}

const getGetFileContentByBlobURLQueryOptions = <
  TData = Awaited<ReturnType<typeof getFileContentByBlobURL>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetDocumentContentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileContentByBlobURL>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  }
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey =
    queryOptions?.queryKey ?? getGetFileContentByBlobURLQueryKey(params)

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getFileContentByBlobURL>>
  > = ({ signal }) => getFileContentByBlobURL(params, signal)

  return {
    queryKey,
    queryFn,
    enabled: !!params.signedUrl && !!params.documentId && !!params.versionId,
    staleTime: Infinity,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getFileContentByBlobURL>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> }
}

/**
 * @summary Get file content by blob URL
 */
export const useGetFileContentByBlobURL = <
  TData = Awaited<ReturnType<typeof getFileContentByBlobURL>>,
  TError = ErrorType<ErrorResponse>,
>(
  params: GetDocumentContentParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileContentByBlobURL>>,
        TError,
        TData
      >
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>
} => {
  const queryOptions = getGetFileContentByBlobURLQueryOptions(params, options)

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & {
    queryKey: DataTag<QueryKey, TData, TError>
  }

  query.queryKey = queryOptions.queryKey

  return query
}
